import { Form, FormikProvider, useFormik } from "formik";
import classNames from "classnames";
import { z } from "zod";
import {
    Ledger,
    LEDGER_NAME_MAX_LENGTH,
} from "@common/contracts/ledger.contract";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { Entity } from "@common/types/entity";
import { useDuplicateLedger, useUpdateLedger } from "@api/ledger.api";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CheckboxIndicator } from "@design-system/Checkbox/CheckboxIndicator";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./AccountingSettings.module.scss";

const LedgerFormSchema = z.object({
    entityId: z.coerce.number(),
    name: z
        .string()
        .trim()
        .min(1, "Ledger name is required")
        .max(LEDGER_NAME_MAX_LENGTH),
    basis: z.nativeEnum(AccountingLedgerBasis),
    isPrimary: z.boolean(),
});

type LedgerFormValues = z.infer<typeof LedgerFormSchema>;

const fields = createFormikFields<LedgerFormValues>();

interface Props {
    mode: "edit" | "duplicate";
    entities: Entity[];
    ledger?: Ledger;
    show: boolean;
    onHide: () => void;
}

export const LedgerFormModal: React.FC<Props> = ({
    mode,
    entities,
    ledger,
    show,
    onHide,
}) => {
    const { error: errorToast } = useToaster();
    const updateLedger = useUpdateLedger();
    const duplicateLedger = useDuplicateLedger();
    const isDuplicateMode = mode === "duplicate";
    const isSubmitting = updateLedger.isPending || duplicateLedger.isPending;

    const formik = useFormik<LedgerFormValues>({
        initialValues: {
            entityId: ledger?.entityId ?? entities[0]?.id ?? 0,
            name: ledger
                ? getInitialLedgerName({ ledger, isDuplicateMode })
                : "",
            basis: ledger?.basis ?? AccountingLedgerBasis.accruals,
            isPrimary: ledger?.isPrimary ?? false,
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(LedgerFormSchema),
        validateOnMount: true,
        onSubmit: submitHelper({
            loading: isSubmitting,
            handler: async (values) => {
                if (!ledger) {
                    return;
                }

                if (isDuplicateMode) {
                    await duplicateLedger.mutateAsync({
                        params: {
                            entityId: ledger.entityId,
                            ledgerId: ledger.id,
                        },
                        body: { name: values.name.trim() },
                    });
                } else {
                    await updateLedger.mutateAsync({
                        params: {
                            entityId: ledger.entityId,
                            ledgerId: ledger.id,
                        },
                        body: {
                            name: values.name.trim(),
                            isPrimary: values.isPrimary,
                        },
                    });
                }

                formik.resetForm();
                onHide();
            },
            failureHandler: async () => {
                errorToast(
                    isDuplicateMode
                        ? "Failed to duplicate ledger"
                        : "Failed to update ledger",
                );
            },
        }),
    });

    const selectedEntity = entities.find(
        (entity) => entity.id === formik.values.entityId,
    );
    const entityItems = selectedEntity
        ? [{ value: selectedEntity.id, label: selectedEntity.name ?? "" }]
        : [];
    const handlePrimaryToggle = () => {
        void formik.setFieldValue("isPrimary", !formik.values.isPrimary);
    };
    const handleHide = () => {
        formik.resetForm();
        onHide();
    };

    return (
        <PlainModal show={show && !!ledger} onHide={handleHide} size="md">
            <StandardModalHeader>
                {isDuplicateMode ? "Duplicate ledger" : "Edit ledger"}
            </StandardModalHeader>
            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    {isDuplicateMode && ledger && (
                        <p className={styles.ledgerModalDescription}>
                            Creates a full copy of {ledger.name}. The new ledger
                            keeps the same accounting method (
                            {getBasisLabel(ledger.basis)}) and inherits its
                            complete history.
                        </p>
                    )}
                    {isDuplicateMode ? (
                        <VStack gap={16}>
                            <fields.Text
                                name="name"
                                label="Ledger name"
                                placeholder="e.g. Accrual GAAP"
                                size="xl"
                            />
                            <DuplicateLedgerCopyList />
                        </VStack>
                    ) : (
                        <VStack gap={16}>
                            <FormikField
                                name="entityId"
                                label="Entity"
                                control="button"
                            >
                                {() => (
                                    <Picker.Select
                                        items={entityItems}
                                        value={selectedEntity?.id ?? null}
                                        renderValue={() =>
                                            selectedEntity ? (
                                                <EntityDisplay
                                                    entity={selectedEntity}
                                                />
                                            ) : undefined
                                        }
                                        placeholder="Select entity"
                                        disabled
                                        trigger={
                                            <Picker.Button
                                                variant="primary"
                                                size="xl"
                                            />
                                        }
                                    />
                                )}
                            </FormikField>
                            <VStack gap={16}>
                                <fields.Text
                                    name="name"
                                    label="Ledger name"
                                    placeholder="e.g. Accrual GAAP"
                                    size="xl"
                                />
                                <VStack gap={8}>
                                    <div>
                                        <span className={styles.methodLabel}>
                                            Accounting method
                                        </span>
                                        <RadioGroup
                                            name="basis"
                                            aria-label="Accounting method"
                                            value={formik.values.basis}
                                            disabled
                                            className={classNames(
                                                styles.methodOptions,
                                                styles.disabledMethodOptions,
                                            )}
                                        >
                                            <Radio
                                                value={
                                                    AccountingLedgerBasis.cash
                                                }
                                                className={styles.methodOption}
                                                label={
                                                    <span>
                                                        <span className="font-weight-medium">
                                                            Cash
                                                        </span>
                                                        <small>
                                                            Record income and
                                                            expenses when money
                                                            moves
                                                        </small>
                                                    </span>
                                                }
                                            />
                                            <Radio
                                                value={
                                                    AccountingLedgerBasis.accruals
                                                }
                                                className={styles.methodOption}
                                                label={
                                                    <span>
                                                        <span className="font-weight-medium">
                                                            Accrual
                                                        </span>
                                                        <small>
                                                            Record income and
                                                            expenses when earned
                                                        </small>
                                                    </span>
                                                }
                                            />
                                        </RadioGroup>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.primaryOption}
                                        role="checkbox"
                                        aria-checked={formik.values.isPrimary}
                                        onClick={handlePrimaryToggle}
                                    >
                                        <CheckboxIndicator
                                            state={
                                                formik.values.isPrimary
                                                    ? "checked"
                                                    : "unchecked"
                                            }
                                            className="mt-1"
                                        />
                                        <span>
                                            <span className="font-weight-medium">
                                                Set as primary ledger
                                            </span>
                                            <small>
                                                Other ledgers with this method
                                                will mirror changes going
                                                forward
                                            </small>
                                        </span>
                                    </button>
                                </VStack>
                            </VStack>
                        </VStack>
                    )}
                    <HStack as="footer" justify="end" gap={12}>
                        <Button
                            variant="secondary"
                            size="xl"
                            type="button"
                            onClick={handleHide}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <ButtonWithLoader
                            variant="primary"
                            size="xl"
                            type="submit"
                            loading={isSubmitting}
                            disabled={!formik.isValid}
                        >
                            {isDuplicateMode ? "Duplicate" : "Save"}
                        </ButtonWithLoader>
                    </HStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};

const DuplicateLedgerCopyList: React.FC = () => (
    <div className={styles.copyList}>
        <p>What gets copied</p>
        <ul>
            <li>Transaction categorization</li>
            <li>Bill and invoice categorization</li>
            <li>Journal entries</li>
            <li>Opening balances</li>
            <li>Chart of accounts</li>
            <li>Accounting rules</li>
        </ul>
    </div>
);

function getInitialLedgerName({
    ledger,
    isDuplicateMode,
}: {
    ledger: Ledger;
    isDuplicateMode: boolean;
}): string {
    return isDuplicateMode ? `${ledger.name} (copy)` : ledger.name;
}

function getBasisLabel(basis: AccountingLedgerBasis): string {
    return basis === AccountingLedgerBasis.cash ? "Cash" : "Accrual";
}
